import { singleLineEllipsisStyles } from "@/styles/utils"

import { Box, Stack, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import type { SxProps, Theme } from "@mui/material/styles"

import { PercentChange } from "../percent-change"
import type { MetricProps } from "./metric.types"

/**
 * Displays a metric with a title and a value.
 *
 * ```tsx
 * <Metric
 *   percentChange={20}
 *   title="Total Cost"
 * >
 *  <Typography>
 *   <Currency
 *    format={{ precision: 0 }}
 *    value={totalCost}
 *   />
 *  </Typography>
 * </Metric>
 * ```
 */
export const Metric = ({
  percentChange,
  children,
  "data-e2e": dataE2e,
  sx,
  title,
}: MetricProps) => {
  const theme = useTheme()
  const baseSx: SxProps<Theme> = {
    borderRadius: 2,
    px: 2,
    py: 1.5,
    backgroundColor: theme.palette.grey[25],
  }

  return (
    <Box sx={[baseSx, sx] as SxProps<Theme>}>
      <Stack direction="column">
        <Typography
          gutterBottom
          sx={[
            {
              color: "text.secondary",
            },
            ...(Array.isArray(singleLineEllipsisStyles)
              ? singleLineEllipsisStyles
              : [singleLineEllipsisStyles]),
          ]}
        >
          {title}
        </Typography>
        <Box data-e2e={dataE2e}>{children}</Box>
        {percentChange !== undefined && <PercentChange value={percentChange} />}
      </Stack>
    </Box>
  )
}
